import { ListItem, Typography } from '@material-ui/core';
import { GridRowParams } from '@material-ui/data-grid';
import { Api } from 'api';
import NameValueRow from 'components/cards/CardRows/NameValueRow';
import { NameValueRowList } from 'components/cards/CardRows/NameValueRowList';
import { TitleSubtitleRow } from 'components/cards/CardRows/TitleSubtitleRow';
import StyledCard from 'components/cards/StyledCard';
import StyledCardContent from 'components/cards/StyledCardContent';
import StyledCardHeader, { HeaderButtonIcon } from 'components/cards/StyledCardHeader';
import { getUserAutocompleteInput } from 'components/forms/UserEmailAutocomplete';
import VMDataGrid from 'components/main/VMDataGrid';
import ActionsList from 'components/other/ActionsList';
import ActionsMenu from 'components/other/ActionsMenu';
import { makeAutoObservable, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { onClickShowCart } from 'pages/Payments/InvoicesPage';
import { formatToSloDate } from 'pages/Sessions/SessionsPage';
import { getBillingPlanInputs } from 'pages/Users/UserDetailsBilling';
import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import credentialStore from 'state/credentialStore';
import dashboardStore from 'state/dashboardStore';
import { drawerStore } from 'state/drawerStore';
import { useProvider } from 'state/providerStore';
import { useStyles } from 'styles/styles';
import { FormInputTypes } from 'utils/constants';
import { gridColumn } from 'utils/gridUtils';
import { filterValuesByInputsArray } from 'utils/utils';

const detailsRows = [
  'id',
  'name',
  'VAT',
  'address',
  'city',
  'zip',
  'country',
  'ownerName',
  { name: 'accountId', label: 'company' },
  'ownerType',
];

const editWalletInputs = [
  { id: 'id', type: FormInputTypes.disabledTextField },
  { name: 'ownerName', type: FormInputTypes.textField },
  { name: 'address', type: FormInputTypes.googleAddressAutocomplete },
  { name: 'zip', type: FormInputTypes.textField },
  { name: 'city', type: FormInputTypes.textField },
  { name: 'country', type: FormInputTypes.textField },
  { name: 'name', type: FormInputTypes.textField },
  { name: 'VAT', type: FormInputTypes.textField },
];

const onUserAddInputChange = (isAdmin, usersProvider, inputValue, purchaseMethodsProvider) => {
  const urlParams: any = { email: inputValue };

  usersProvider.setOnFulfilled(() => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    drawerStore.setInputs(getUserAddInputs(isAdmin, usersProvider, purchaseMethodsProvider));
    drawerStore.setValues({ ...drawerStore.values });
  });

  usersProvider.setRequest(() => Api.user.listUsers(urlParams));
  usersProvider.reloadResource();
};

function getUserAddInputs(isAdmin, usersProvider, purchaseMethodsProvider) {
  const users = [];
  usersProvider.data?.users?.forEach((user) => {
    const alreadyAddedUser = purchaseMethodsProvider.data?.find(
      (method) => method.userId === user.id,
    );
    if (!alreadyAddedUser) {
      users.push(user);
    }
  });

  if (isAdmin) {
    return [
      getUserAutocompleteInput(users, (inputValue) =>
        onUserAddInputChange(isAdmin, usersProvider, inputValue, purchaseMethodsProvider),
      ),
    ];
  } else {
    return [{ name: 'userId', type: FormInputTypes.textField }];
  }
}

const balanceCorrectionInputs = [
  { name: 'amount', type: FormInputTypes.number },
  { name: 'note', type: FormInputTypes.multilineTextField },
];

class WalletStore {
  // Purchase methods with user data added from separate requests
  methods: any[] = [];

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  addMethodWithUserData(method, user) {
    this.methods.push({ ...method, user });
  }

  resetMethods() {
    this.methods = [];
  }
}

const transactionActions = (params: GridRowParams) => {
  const cartId = params.row?.cartId;
  if (!cartId) {
    return <div />;
  }

  return (
    <ActionsMenu>
      {cartId && (
        <ListItem button onClick={() => onClickShowCart(cartId)}>
          <Typography style={{ width: '100%' }}>View Cart</Typography>
        </ListItem>
      )}
    </ActionsMenu>
  );
};

export const WalletDetails = observer(() => {
  const { walletid } = useParams<{ walletid: string }>();
  const classes = useStyles();

  const [walletStore] = useState(() => new WalletStore());
  const billingPlanProvider = useProvider(() => Api.billing.getBillingPlanForUser(walletid));
  const paymentMethodsProvider = useProvider(() => Api.billing.getPaymentMethodsForUser(walletid));

  const [source, setSource] = React.useState(undefined);

  const history = useHistory();

  useEffect(() => {
    dashboardStore.appBarLabel = 'Wallet details';
  }, [history.location]);

  const queryUsersFromCognito = (methods: any[]) => {
    walletStore.resetMethods();

    methods.forEach((method) => {
      Api.user
        .getUser(method.userId)
        .then((resp) => walletStore.addMethodWithUserData(method, resp.data.data));
    });
  };

  const onPurchaseMethodsSuccess = (data) => {
    queryUsersFromCognito(data);
  };

  const walletProvider = useProvider(() => Api.billing.getWallet(walletid));
  const transactionsProvider = useProvider(() => Api.billing.listTransactionsForWallet(walletid));
  const purchaseMethodsProvider = useProvider(
    () => Api.billing.listPurchaseMethodsForWallet(walletid),
    onPurchaseMethodsSuccess,
  );
  const usersProvider = useProvider(Api.user.listUsers, undefined, false);

  useEffect(() => {
    if (credentialStore.isUserAdmin) {
      usersProvider.reloadResource();
    }
  }, [credentialStore.isUserAdmin]);

  const confirmUpdateWallet = () => {
    const values = filterValuesByInputsArray(toJS(drawerStore.values), editWalletInputs);
    Api.billing.updateWallet(walletid, values).then(walletProvider.reloadResource);
  };

  const confirmEditBillingPlan = () => {
    Api.billing.updateBillingPlanForUser(walletid, drawerStore.values).then(() => {
      billingPlanProvider.reloadResource();
    });
  };

  const onClickEditDetails = () => {
    drawerStore.openDrawer({
      label: 'Update wallet',
      inputs: editWalletInputs,
      clickCallback: confirmUpdateWallet,
    });
    drawerStore.setValues(walletProvider.data);
  };

  const confirmAddUserToWallet = (values) => {
    Api.billing
      .createPurchaseMethodForWallet(walletid, { userId: values.userId })
      .then(purchaseMethodsProvider.reloadResource);
  };

  const onClickAddUserToWallet = () => {
    drawerStore.openDrawer({
      label: 'Add user',
      inputs: getUserAddInputs(credentialStore.isUserAdmin, usersProvider, purchaseMethodsProvider),
      clickCallback: () => confirmAddUserToWallet(toJS(drawerStore.values)),
    });
  };

  const confirmCorrectBalance = (values) => {
    Api.billing.correctBalance(walletid, { amount: values.amount, note: values.note }).then(() => {
      walletProvider.reloadResource();
      transactionsProvider.reloadResource();
    });
  };

  const onClickAddBalance = () => {
    drawerStore.openDrawer({
      label: 'Balance correction',
      inputs: balanceCorrectionInputs,
      clickCallback: () => confirmCorrectBalance(toJS(drawerStore.values)),
    });
  };

  const transactionColumns = [
    gridColumn('createdAt', {
      flex: 1,
      minWidth: 150,
      headerName: 'Date',
      renderCell: (params: GridRowParams) => {
        const { createdAt } = params.row;
        const formattedDate = formatToSloDate(createdAt);
        return <div>{formattedDate}</div>;
      },
    }),
    gridColumn('reason', {
      flex: 1,
      minWidth: 200,
      headerName: 'Type',
      renderCell: (params: GridRowParams) => {
        return (
          <div style={{ textAlign: 'right', textTransform: 'capitalize' }}>
            {params.row.reason?.replace(/_/g, ' ')}
          </div>
        );
      },
    }),
    gridColumn('note', { flex: 1 }),
    gridColumn('amount', {
      flex: 1,
      minWidth: 150,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRowParams) => {
        const { amount } = params.row;
        return <div style={{ textAlign: 'right' }}>{amount.formatted}</div>;
      },
    }),
    gridColumn('balance', {
      flex: 1,
      minWidth: 150,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRowParams) => {
        const { balance } = params.row;
        return <div>{balance.formatted}</div>;
      },
    }),
    gridColumn('actions', {
      headerName: '',
      width: 80,
      align: 'right',
      renderCell: transactionActions,
    }),
  ];

  const onClickRemoveUserFromWallet = (method) => {
    Api.billing.deletePurchaseMethod(method.id).then(purchaseMethodsProvider.reloadResource);
  };

  const onClickEditBillingPlan = () => {
    const newValues = { ...billingPlanProvider.data };
    if (newValues.minBalance) {
      newValues.minBalance = newValues.minBalance.value;
    }

    drawerStore.openDrawer({
      label: 'Billing Plan',
      inputs: getBillingPlanInputs(paymentMethodsProvider),
      clickCallback: confirmEditBillingPlan,
      buttonLabel: 'Save',
    });
    drawerStore.setValues(newValues);
  };

  useEffect(() => {
    if (paymentMethodsProvider.data) {
      const settlementSource = billingPlanProvider.data?.settlementSource;
      if (settlementSource) {
        const purchaseMethod = paymentMethodsProvider.data?.find(
          (method) => method.id === settlementSource,
        );
        if (purchaseMethod?.walletId) {
          Api.billing.getWallet(purchaseMethod?.walletId).then((res) => {
            setSource(res.data.data);
          });
        }
      }
    }
  }, [billingPlanProvider.data, paymentMethodsProvider.data]);

  const isMonthly = billingPlanProvider.data?.settlementInterval === 'monthly';
  const isPaymentMethod = billingPlanProvider.data?.settlementMode === 'payment_method';

  return (
    <div style={{ width: '100%' }}>
      <div className={classes.cardRows}>
        <div className={classes.leftCardRow}>
          <StyledCard>
            <StyledCardHeader
              title="Details"
              onClick={onClickEditDetails}
              headerButtonIcon={HeaderButtonIcon.edit}
            />
            <StyledCardContent>
              <NameValueRowList rows={detailsRows} values={walletProvider.data} hideEmpty />
            </StyledCardContent>
          </StyledCard>
        </div>
        <div className={classes.rightCardRow}>
          <StyledCard>
            <StyledCardHeader
              title="Balance"
              onClick={credentialStore.isUserAdmin ? onClickAddBalance : undefined}
              headerButtonIcon={HeaderButtonIcon.add}
            />
            <StyledCardContent>
              <NameValueRow name="balance" value={walletProvider.data?.balance?.formatted} />
            </StyledCardContent>
          </StyledCard>

          <StyledCard>
            <StyledCardHeader
              title="Users"
              onClick={onClickAddUserToWallet}
              headerButtonIcon={HeaderButtonIcon.add}
            />
            <StyledCardContent>
              {walletStore.methods.map((method) => {
                const fullName = `${method.user?.name} ${method.user?.familyName}`;

                return (
                  <div key={method.id}>
                    <TitleSubtitleRow
                      title={fullName}
                      subtitle={method.user?.email}
                      actions={
                        <ActionsList
                          actions={[
                            {
                              label: 'Delete',
                              callback: () => onClickRemoveUserFromWallet(method),
                            },
                          ]}
                        />
                      }
                    />
                  </div>
                );
              })}
            </StyledCardContent>
          </StyledCard>
          <StyledCard>
            <StyledCardHeader
              title="Billing Plan"
              onClick={onClickEditBillingPlan}
              headerButtonIcon={HeaderButtonIcon.edit}
            />
            <StyledCardContent>
              <NameValueRow name="INTERVAL" value={billingPlanProvider.data?.settlementInterval} />
              {isMonthly && billingPlanProvider.data?.minBalance && (
                <NameValueRow
                  name="Max credit"
                  value={billingPlanProvider.data?.minBalance?.formatted}
                />
              )}
              {isMonthly && (
                <NameValueRow name="Settlement" value={billingPlanProvider.data?.settlementMode} />
              )}
              {isMonthly && isPaymentMethod && <NameValueRow name="Source" value={source?.name} />}
              {isMonthly && isPaymentMethod && (
                <NameValueRow name="Balance" value={source?.balance?.formatted} />
              )}
            </StyledCardContent>
          </StyledCard>
        </div>
      </div>

      <VMDataGrid
        rows={transactionsProvider.data}
        columns={transactionColumns}
        label="Transactions"
      />
    </div>
  );
});
